// src/composables/useOrganizationSetup.js
//
// Small shared source of truth for organization branding (name + logo).
// `/setup` is a public endpoint, so this is safe to call from the login
// page as well as authenticated pages like the sidebar and footer.
//
// The state is defined at module scope (outside the exported function) so
// every component that calls useOrganizationSetup() shares the same refs —
// fetch once, read everywhere, no duplicate network calls or prop drilling.

import { ref } from 'vue'

const baseApi = process.env.VUE_APP_BASE_API ?? ''

const setup = ref(null)
const loading = ref(false)
const loaded = ref(false)

const DEFAULT_NAME = 'Nexara'
const DEFAULT_SUB = 'Human Resources'

const fetchSetup = async ({ force = false } = {}) => {
  if (loaded.value && !force) return setup.value

  loading.value = true
  try {
    const res = await fetch(`${baseApi}/setup`)
    const data = await res.json().catch(() => ({}))
    setup.value = data?.setup ?? null
  } catch {
    setup.value = null
  } finally {
    loading.value = false
    loaded.value = true
  }
  return setup.value
}

export function useOrganizationSetup() {
  const organizationName = () => setup.value?.organization_name || DEFAULT_NAME
  const organizationSub = () => DEFAULT_SUB
  const organizationLogoUrl = () => setup.value?.organization_logo_url || null

  return {
    setup,
    loading,
    loaded,
    fetchSetup,
    organizationName,
    organizationSub,
    organizationLogoUrl,
  }
}